import {
  Body1Strong,
  Card,
  Checkbox,
  CheckboxOnChangeData,
  InputOnChangeData,
  mergeClasses,
  SearchBox,
  SearchBoxChangeEvent,
  SelectTabData,
  SelectTabEvent,
  Tab,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabList,
  TabValue,
} from '@fluentui/react-components';
import {
  ESubmissionItemStatus,
  ESubmissionStatus,
  TResolvedSubmissionItem,
  TSubmission,
} from '../../../types/submissions.types';
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { formatSubmissionStatus } from '../../SubmissionStatusBadge';
import {
  ArrowDownloadRegular,
  ArrowUndoRegular,
  BoxMultipleCheckmarkRegular,
  CheckmarkRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { SignInContext } from '@modules/users';
import { SUBMISSION_ITEMS_TAB_VALUE_ALL, SUBMISSION_ITEMS_TABLE_COLUMN_IDS } from '../constants';
import { useSelectableTable } from '@shared/hooks';
import { TExportDialogState, useItemTableStyles } from '@modules/items';
import SubmissionItem from './SubmissionItem';
import UpdateSubmissionItemDialog from '../dialogs/UpdateSubmissionItemDialog';
import { useBulkExportSubmission, useSelectSubmissionItems, useTransitionSubmission } from '../../../api';
import { ButtonLoading, ControlledTooltip } from '@shared/components';
import { TUpdateSubmissionItemDialogState } from '../../../types/dialogs.types';
import { ExportDialog } from '@modules/items';
import { useHasFullPermissions } from '../../../helpers/getPermissions.helper';
import { getSubmissionPackageMockConfig, SubmissionPackageDialog } from '@modules/mocks/submissionPackage';

type TProps = {
  submission: TSubmission;
  resolvedSelectedItems: TResolvedSubmissionItem[];
};

const SelectedItems = ({ submission, resolvedSelectedItems }: TProps) => {
  const { user, organization } = useContext(SignInContext);

  // --- FILTERING:

  // Tabs to filter by status:
  const [selectedTab, setSelectedTab] = useState<TabValue>(SUBMISSION_ITEMS_TAB_VALUE_ALL);
  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => setSelectedTab(data.value);

  // Searchbox to filter by name:
  const [searchValue, setSearchValue] = useState('');
  const handleSearchChange = (ev: SearchBoxChangeEvent, data: InputOnChangeData) => setSearchValue(data.value);

  // Checkbox to filter by assigned items:
  const [onlyAssigned, setOnlyAssigned] = useState(false);
  const handleAssignedChange = (ev: ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) =>
    setOnlyAssigned(Boolean(data.checked));

  // filter resolved-items based on filters above:
  const filteredResolvedSelectedItems = useMemo(
    () =>
      resolvedSelectedItems.filter(
        (resolvedSelectedItem) =>
          (selectedTab === SUBMISSION_ITEMS_TAB_VALUE_ALL || resolvedSelectedItem.status === selectedTab) &&
          (!onlyAssigned || user._id === resolvedSelectedItem.assignee) &&
          (searchValue.length <= 0 ||
            resolvedSelectedItem.resolvedItem.name.toLowerCase().includes(searchValue.toLowerCase()))
      ),
    [resolvedSelectedItems, selectedTab, searchValue, onlyAssigned, user._id]
  );

  // --- TABLE:

  // table rows checked by the user:
  const [checkedItemIds, setCheckedItemIds] = useState(new Set<string>());

  // apply deselect to checkedItemIds state:
  const selectedItemIds = resolvedSelectedItems.map((resolvedSubmissionItem) => resolvedSubmissionItem.item);
  useEffect(() => {
    const newCheckedItemIds = new Set([...checkedItemIds].filter((x) => selectedItemIds.includes(x)));
    if (newCheckedItemIds.size !== checkedItemIds.size) setCheckedItemIds(newCheckedItemIds);
  }, [selectedItemIds]); // specific dependency to avoid unnecessary re-renders

  const { selectionHeaderCell, tableRows, columnSizing } = useSelectableTable(
    SUBMISSION_ITEMS_TABLE_COLUMN_IDS,
    filteredResolvedSelectedItems,
    (resolvedItem) => resolvedItem.item,
    checkedItemIds,
    setCheckedItemIds,
    {
      resizable: true,
      tableId: 'submission-items-table',
      columnSizingParams: {
        autoFitColumns: false,
        columnSizingOptions: {
          name: { idealWidth: 300, minWidth: 150 },
          status: { defaultWidth: 75 },
          assignee: { defaultWidth: 75 },
          dueDate: { defaultWidth: 75 },
          submittedVersion: { defaultWidth: 75 },
          latestVersion: { defaultWidth: 75 },
          approvedVersion: { defaultWidth: 75 },
        },
      },
    }
  );

  // dialogs:
  const [updateSubmissionItemDialogState, setUpdateSubmissionItemDialogState] = useState<
    TUpdateSubmissionItemDialogState | undefined
  >(undefined);

  // --- DESELECT:
  const selectSubmissionItems = useSelectSubmissionItems(submission._id);
  const handleDeselect = () => {
    if (checkedItemIds.size <= 0) return;

    // checkedItemIds is updated by useEffect above
    selectSubmissionItems.mutate(
      submission.selectedItems
        .filter((submissionItem) => !checkedItemIds.has(submissionItem.item) && submissionItem.active)
        .map((item) => ({ itemId: item.item, itemType: item.itemType }))
    );
  };

  // --- EXPORT:
  const bulkExportSubmission = useBulkExportSubmission();
  const [exportDialogState, setExportDialogState] = useState<TExportDialogState | undefined>(undefined);
  const submissionPackageConfig = getSubmissionPackageMockConfig(organization.mock?.submissionPackage, submission._id);
  const [isSubmissionPackageDialogOpen, setIsSubmissionPackageDialogOpen] = useState(false);
  const handleExport = () => {
    let checkedItems = resolvedSelectedItems.filter((resolvedSelectedItem) =>
      checkedItemIds.has(resolvedSelectedItem.item)
    );
    if (checkedItems.length <= 0) checkedItems = resolvedSelectedItems;
    const checkedResolvedItems = checkedItems.map((resolvedSelectedItem) => resolvedSelectedItem.resolvedItem);
    const numMissingSubmittedVersion = checkedItems.filter(
      (resolvedSelectedItem) => !resolvedSelectedItem.submittedVersionId
    ).length;

    if (numMissingSubmittedVersion > 0) {
      setExportDialogState({
        isOpen: true,
        affectedItems: checkedResolvedItems,
        submissionId: submission._id,
        message: `For ${numMissingSubmittedVersion} item${numMissingSubmittedVersion === 1 ? '' : 's'} no version is set for submission.`,
      });
    } else {
      bulkExportSubmission.mutate({
        submissionId: submission._id,
        mode: 'DRAFT',
        itemIds: checkedItemIds.size > 0 ? Array.from(checkedItemIds) : undefined,
      });
    }
  };

  // --- TRANSITION SUBMISSION:
  const isPreparation = submission.status === ESubmissionStatus.IN_PREPARATION;
  const transitionSubmission = useTransitionSubmission();
  const handleTransition = () => {
    transitionSubmission.mutate({
      submissionId: submission._id,
      updateData: {
        newStatus: isPreparation ? ESubmissionStatus.DONE : ESubmissionStatus.IN_PREPARATION,
      },
    });
  };

  // --- Permissions:
  const hasFullPermissions = useHasFullPermissions(submission);

  // --- RENDER:
  const classes = useItemTableStyles();

  return (
    <div>
      <UpdateSubmissionItemDialog
        state={updateSubmissionItemDialogState}
        setState={setUpdateSubmissionItemDialogState}
      />
      <ExportDialog state={exportDialogState} setState={setExportDialogState} />
      <SubmissionPackageDialog
        config={submissionPackageConfig}
        open={isSubmissionPackageDialogOpen}
        setOpen={setIsSubmissionPackageDialogOpen}
      />

      <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect} size="small" className="-ml-1.5">
        <Tab value={SUBMISSION_ITEMS_TAB_VALUE_ALL}>All files</Tab>
        {[
          ESubmissionItemStatus.BACKLOG,
          ESubmissionItemStatus.IN_PROGRESS,
          ESubmissionItemStatus.IN_REVIEW,
          ESubmissionItemStatus.ACCEPTED,
        ].map((status) => (
          <Tab key={status} value={status}>
            {formatSubmissionStatus(status)} ({resolvedSelectedItems.filter((item) => item.status === status).length})
          </Tab>
        ))}
      </TabList>

      <div className="flex items-center justify-between mt-6">
        <div className="flex items-center gap-4">
          <SearchBox
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search files"
            appearance="outline"
          />
          <Checkbox label="Assigned to me" checked={onlyAssigned} onChange={handleAssignedChange} />
        </div>

        <div className="flex gap-4">
          {hasFullPermissions && checkedItemIds.size > 0 && (
            <ButtonLoading
              icon={<DismissRegular />}
              appearance="secondary"
              loading={selectSubmissionItems.isPending}
              onClick={handleDeselect}
            >
              Remove {checkedItemIds.size === 1 ? '1 item' : `${checkedItemIds.size} items`}
            </ButtonLoading>
          )}

          {submissionPackageConfig ? (
            <ButtonLoading
              icon={<BoxMultipleCheckmarkRegular />}
              appearance="secondary"
              onClick={() => setIsSubmissionPackageDialogOpen(true)}
            >
              Generate submission package
            </ButtonLoading>
          ) : (
            <ButtonLoading
              icon={<ArrowDownloadRegular />}
              appearance="secondary"
              loading={bulkExportSubmission.isPending}
              onClick={handleExport}
            >
              Export{' '}
              {checkedItemIds.size <= 0 ? 'all' : checkedItemIds.size === 1 ? '1 item' : `${checkedItemIds.size} items`}
            </ButtonLoading>
          )}

          {hasFullPermissions && (
            <ControlledTooltip
              enable={isPreparation}
              relationship="inaccessible"
              content="Complete the submission and freeze its content."
            >
              <ButtonLoading
                icon={isPreparation ? <CheckmarkRegular /> : <ArrowUndoRegular />}
                appearance="primary"
                loading={transitionSubmission.isPending}
                onClick={handleTransition}
              >
                {isPreparation ? 'Done' : 'Reopen'}
              </ButtonLoading>
            </ControlledTooltip>
          )}
        </div>
      </div>

      <Card className={mergeClasses(classes.card, 'mt-2')}>
        <Table className={classes.table} {...columnSizing?.getTableProps()}>
          <TableHeader>
            <TableRow>
              {selectionHeaderCell}
              <TableHeaderCell {...columnSizing?.getTableHeaderCellProps('name')}>
                <Body1Strong>Name</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell {...columnSizing?.getTableHeaderCellProps('status')}>
                <Body1Strong>Review status</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell {...columnSizing?.getTableHeaderCellProps('assignee')}>
                <Body1Strong>Assignee</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell {...columnSizing?.getTableHeaderCellProps('dueDate')}>
                <Body1Strong>Due date</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell {...columnSizing?.getTableHeaderCellProps('submittedVersion')}>
                <Body1Strong>Version to submit</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell {...columnSizing?.getTableHeaderCellProps('latestVersion')}>
                <Body1Strong>Latest version</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell {...columnSizing?.getTableHeaderCellProps('approvedVersion')}>
                <Body1Strong>Approved version</Body1Strong>
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody className={classes.tableBody}>
            {tableRows.map((tableRow) => (
              <SubmissionItem
                {...tableRow}
                columnSizing={columnSizing}
                key={tableRow.key}
                submission={submission}
                filteredResolvedSelectedItems={filteredResolvedSelectedItems}
                setUpdateSubmissionItemDialogState={setUpdateSubmissionItemDialogState}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default SelectedItems;
